import _, { set } from 'lodash'
import { Streak } from '../utils/calcStreak'
import { habitTypeMap, regularTargetValue } from '../utils/constants'
import { MiwProgress, Target } from '../utils/dataFormatting'
import { findKey } from '../utils/misc'
import Confejji from './Confejji'
import { StreakBubble } from './StreakBubble'
import { Habit, NewHabitType, OldHabitType } from './TargetScores'
import { Button } from '@mui/material'
import { CSSProperties, useCallback, useState } from 'react'

export const habitTypes: NewHabitType[] = [
  'sleep',
  'plannedDay',
  'miw',
  'fitness',
  'nutrition',
  'personalChallenge',
  'love',
]

const ButtonWrapper = ({
  onClick,
  style,
  className,
  children,
}: {
  onClick: () => Promise<void>
  style: CSSProperties
  className: string
  children: React.ReactNode
}) => {
  const [loading, setLoading] = useState(false)

  const handleClick = useCallback(async () => {
    if (loading) return
    setLoading(true)
    await onClick()
    setLoading(false)
  }, [loading, onClick])

  return (
    <div
      onClick={handleClick}
      style={{ ...style, opacity: loading ? 0.5 : 1 }}
      className={className}
    >
      {children}
    </div>
  )
}

interface HabitsPanelProps {
  targets: Target[]
  habits: Habit[]
  streaks?: Streak[]
  isViewOnly?: boolean
  isInEditMode?: boolean
  onClickHabit?: (habit: Habit) => Promise<void>
  activeHabit?: OldHabitType
  isShowingConfejji?: boolean
  playerHasSentHighFiveToday?: boolean
  onClickHighFive?: () => void
  teammateName?: string
}

const HabitsPanel = ({
  habits,
  targets,
  streaks,
  isViewOnly,
  isInEditMode,
  onClickHabit,
  activeHabit,
  isShowingConfejji,
  playerHasSentHighFiveToday,
  onClickHighFive,
  teammateName,
}: HabitsPanelProps) => {
  const handleClick = async (habit: Habit) => {
    !isViewOnly && onClickHabit && (await onClickHabit(habit))
  }

  return (
    <div
      className={`HabitsPanel ${
        isInEditMode || isViewOnly ? 'HabitsPanel--vertical' : ''
      }`}
    >
      {habitTypes.map((habitType, index) => {
        const habit = habits.find((h) => habitTypeMap[h.name] === habitType)
        const target = targets.find((t) => t.habitType === habitType)
        const oldHabitType = findKey(
          habitTypeMap,
          (v) => v === target?.habitType
        )

        return (
          <ButtonWrapper
            style={{ position: 'relative' }}
            onClick={async () => habit && handleClick(habit)}
            className={
              `card` +
              (!habit
                ? ''
                : `
                ${
                  !isInEditMode &&
                  habit.score === regularTargetValue &&
                  habit.name !== 'Main Priority' &&
                  'card-green'
                }
                ${activeHabit === habit.name && 'bor'}
                ${
                  !isInEditMode &&
                  (habit.name === 'Main Priority'
                    ? habit.miwProgress === MiwProgress.main
                      ? 'card-green'
                      : habit.miwProgress === MiwProgress.starter
                      ? 'card-yellow'
                      : ''
                    : '')
                }
            `)
            }
            key={index}
          >
            <div style={{ overflow: 'hidden', height: '100%' }}>
              <p className="title">{oldHabitType}</p>
              <p className="text">
                {!target ? 'No target set yet' : target.text}
              </p>
            </div>
            {isShowingConfejji && activeHabit === oldHabitType && target && (
              <Confejji type={target.habitType} />
            )}
            {streaks && (
              <StreakBubble
                streakLength={
                  streaks.find((s) => s.habitType === habitType)?.length || 0
                }
              />
            )}
          </ButtonWrapper>
        )
      })}
      {!isInEditMode && (
        <Button
          className="card"
          variant={!playerHasSentHighFiveToday ? 'contained' : 'outlined'}
          onClick={onClickHighFive}
        >
          {!playerHasSentHighFiveToday
            ? `Send ${teammateName} a high five! 👋`
            : `Chat with ${teammateName}`}
        </Button>
      )}
    </div>
  )
}

export default HabitsPanel
